const darkCodeBackground = "#20211e";
const lightCodeBackground = "#fbfaf6";
const darkCodeForeground = "#22231f";
const lightCodeForeground = "#f0ede4";
const minimumContrast = 7;

type Rgb = [number, number, number];

function parseHex(color: string): Rgb | null {
  const match = /^#([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(color);
  if (!match) return null;
  return [1, 2, 3].map((index) => Number.parseInt(match[index], 16)) as Rgb;
}

function luminance([red, green, blue]: Rgb): number {
  const linear = [red, green, blue].map((channel) => {
    const value = channel / 255;
    return value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
  });
  return .2126 * linear[0] + .7152 * linear[1] + .0722 * linear[2];
}

export function contrastRatio(foreground: string, background: string): number {
  const foregroundRgb = parseHex(foreground);
  const backgroundRgb = parseHex(background);
  if (!foregroundRgb || !backgroundRgb) return 1;
  const lighter = Math.max(luminance(foregroundRgb), luminance(backgroundRgb));
  const darker = Math.min(luminance(foregroundRgb), luminance(backgroundRgb));
  return (lighter + .05) / (darker + .05);
}

function mix(left: Rgb, right: Rgb, amount: number): Rgb {
  return left.map((channel, index) => Math.round(channel + (right[index] - channel) * amount)) as Rgb;
}

function toHex(rgb: Rgb): string {
  return `#${rgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

function accessibleSyntaxColor(color: string, backgroundColor: string, destinationColor: string): string {
  const source = parseHex(color);
  const destination = parseHex(destinationColor)!;
  if (!source || contrastRatio(color, backgroundColor) >= minimumContrast) return color;

  let low = 0;
  let high = 1;
  for (let iteration = 0; iteration < 10; iteration += 1) {
    const amount = (low + high) / 2;
    const candidate = toHex(mix(source, destination, amount));
    if (contrastRatio(candidate, backgroundColor) >= minimumContrast) high = amount;
    else low = amount;
  }
  return toHex(mix(source, destination, high));
}

export function lightSyntaxColor(color: string): string {
  return accessibleSyntaxColor(color, lightCodeBackground, darkCodeForeground);
}

export function darkSyntaxColor(color: string): string {
  return accessibleSyntaxColor(color, darkCodeBackground, lightCodeForeground);
}
